import React, { useState, useEffect, useCallback } from "react";
import { countries } from "../components/CountryName";
import { ToastContainer } from "react-toastify";
import {
    Globe,
    PlusCircle,
    Trash2,
    History,
    CheckCircle,
    AlertCircle,
    Clock,
    Edit3,
    ChevronDown
} from "lucide-react";

// Function to create a fresh copy of empty visa
const createFreshVisa = (emptyVisa) => {
    const newVisa = emptyVisa();
    return { ...newVisa };
};

// Function to create a fresh copy of empty passport
const createFreshPassport = (emptyPassport) => {
    const newPassport = emptyPassport();
    return { ...newPassport };
};

const VISAForm = ({
    visas,
    setVisas,
    oldPassports,
    setOldPassports,
    preview,
    setPreview,
    handleVisaChange,
    handleOldPassportChange,
    loading,
    emptyVisa,
    emptyPassport,
    onSave
}) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [expandedPassports, setExpandedPassports] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    const STATUS_CONFIG = {
        pending: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending Verification" },
        approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <CheckCircle size={14} />, label: "Verified & Approved" },
        reject: { color: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Information Rejected" },
    };

    // Ensure visas is always an array
    const safeVisas = Array.isArray(visas) ? visas : [];
    const safeOldPassports = oldPassports?.passports ? (Array.isArray(oldPassports.passports) ? oldPassports.passports : []) : [];

    // Initialize expanded state for visa cards
    useEffect(() => {
        const initialExpandedState = {};
        safeVisas.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedCards(initialExpandedState);
    }, [safeVisas.length]);

    // Initialize expanded state for passport cards
    useEffect(() => {
        const initialExpandedState = {};
        safeOldPassports.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedPassports(initialExpandedState);
    }, [safeOldPassports.length]);

    const toggleVisaCard = (index) => {
        setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const togglePassportCard = (index) => {
        setExpandedPassports(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const addVisaEntry = useCallback(() => {
        if (setVisas && emptyVisa) {
            setVisas(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newVisa = createFreshVisa(emptyVisa);
                const newArray = [...currentArray, newVisa];
                const newIndex = newArray.length - 1;

                setExpandedCards(prevExpanded => ({
                    ...prevExpanded,
                    [newIndex]: true
                }));

                return newArray;
            });
        }
    }, [setVisas, emptyVisa]);

    const removeVisaEntry = useCallback((i) => {
        if (setVisas) {
            setVisas(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newArray = currentArray.filter((_, index) => index !== i);

                setExpandedCards(prevExpanded => {
                    const newExpanded = {};
                    Object.keys(prevExpanded).forEach(key => {
                        const numKey = parseInt(key);
                        if (numKey < i) {
                            newExpanded[numKey] = prevExpanded[numKey];
                        } else if (numKey > i) {
                            newExpanded[numKey - 1] = prevExpanded[numKey];
                        }
                    });
                    return newExpanded;
                });

                return newArray;
            });
        }
    }, [setVisas]);

    const addPassportEntry = useCallback(() => {
        if (setOldPassports && emptyPassport) {
            setOldPassports(prev => ({
                ...prev,
                hasOldPassport: true,
                passports: [...(prev?.passports || []), createFreshPassport(emptyPassport)]
            }));

            const newIndex = safeOldPassports.length;
            setExpandedPassports(prev => ({ ...prev, [newIndex]: true }));
        }
    }, [setOldPassports, emptyPassport, safeOldPassports.length]);

    const removePassportEntry = useCallback((i) => {
        if (setOldPassports) {
            setOldPassports(prev => ({
                ...prev,
                passports: prev.passports.filter((_, idx) => idx !== i)
            }));

            setExpandedPassports(prevExpanded => {
                const newExpanded = {};
                Object.keys(prevExpanded).forEach(key => {
                    const numKey = parseInt(key);
                    if (numKey < i) {
                        newExpanded[numKey] = prevExpanded[numKey];
                    } else if (numKey > i) {
                        newExpanded[numKey - 1] = prevExpanded[numKey];
                    }
                });
                return newExpanded;
            });
        }
    }, [setOldPassports]);

    const validateForm = () => {
        const newErrors = {};
        safeVisas.forEach((visa, i) => {
            if (visa.denied && !visa.denialReason) {
                newErrors[`denialReason_${i}`] = "Please provide reason for denial";
            }
        });
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving visa records...</p>
                    </div>
                </div>
            )}

            {/* HEADER */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white relative overflow-hidden shadow-xl">
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="p-2 sm:p-3 bg-white/10 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                            <Globe size={24} className="text-blue-300 sm:w-[28px] sm:h-[28px] md:w-[32px] md:h-[32px]" />
                        </div>
                        <div>
                            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Visa Information</h1>
                            <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5 sm:mt-1">International travel document records</p>
                        </div>
                    </div>
                    {preview && !isEditing && (
                        <div className={`flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider shadow-sm ${STATUS_CONFIG[preview.status?.toLowerCase()]?.color || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                            {STATUS_CONFIG[preview.status?.toLowerCase()]?.icon}
                            <span>{STATUS_CONFIG[preview.status?.toLowerCase()]?.label || "Processing"}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-4 sm:p-6 md:p-8">

                {preview && !isEditing ? (
                    /* ================= PREVIEW MODE ================= */
                    <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-500">
                        {preview.status === "approve" && (
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                                <CheckCircle size={20} className="text-green-600 shrink-0" />
                                <p className="text-sm font-medium text-green-800">Visa information verified and approved</p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                            {safeVisas.filter(v => v.country).map((v, i) => (
                                <div key={i} className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-gray-100 hover:shadow-md transition-all">
                                    <div className="flex justify-between items-start mb-3 sm:mb-4">
                                        <div>
                                            <span className="text-[10px] sm:text-[11px] font-semibold text-blue-600 uppercase tracking-wider">
                                                Visa #{i + 1}
                                            </span>
                                            <h3 className="text-base sm:text-lg font-bold text-gray-800 mt-1">{v.country}</h3>
                                        </div>
                                        {v.denied && (
                                            <span className="bg-rose-100 text-rose-700 px-2 py-1 rounded-lg text-[9px] sm:text-[10px] font-semibold">
                                                Previously Denied
                                            </span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                        <PreviewItem label="Visa Type" value={v.visaType} />
                                        <PreviewItem label="Issue Date" value={v.issueDate} isDate />
                                        <PreviewItem label="Expiry Date" value={v.expiryDate} isDate />
                                    </div>
                                    {v.denied && v.denialReason && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                            <PreviewItem label="Denial Reason" value={v.denialReason} />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {oldPassports.hasOldPassport && safeOldPassports.filter(p => p.passportNumber).length > 0 && (
                            <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-5 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl text-white">
                                <h4 className="flex items-center gap-2 font-semibold mb-4 sm:mb-5 text-blue-300 text-xs sm:text-sm uppercase tracking-wider">
                                    <History size={16} /> Previous Passport Records
                                </h4>
                                <div className="space-y-3 sm:space-y-4">
                                    {safeOldPassports.filter(p => p.passportNumber).map((p, i) => (
                                        <div key={i} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 bg-white/10 p-3 sm:p-4 rounded-xl border border-white/10">
                                            <PreviewItem label="Passport Number" value={p.passportNumber} light />
                                            <PreviewItem label="Issue Date" value={p.issueDate} isDate light />
                                            <PreviewItem label="Expiry Date" value={p.expiryDate} isDate light />
                                            <div className="sm:col-span-2 lg:col-span-1">
                                                <PreviewItem label="Remarks" value={p.remarks} light />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(preview.status === "reject" || !preview.status) && (
                            <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4">
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold transition-all shadow-md"
                                >
                                    <Edit3 size={18} /> Modify Records
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    /* ================= FORM MODE ================= */
                    <form onSubmit={handleFormSubmit} className="space-y-8 sm:space-y-10 md:space-y-12">

                        {/* VISA SECTION */}
                        <div className="space-y-5 sm:space-y-6">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                <h3 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                                    <Globe size={16} className="text-blue-600" />
                                    International Visas
                                </h3>
                                <button
                                    type="button"
                                    onClick={addVisaEntry}
                                    className="flex items-center gap-1.5 text-blue-600 font-semibold text-[10px] sm:text-xs hover:text-blue-700 transition-all bg-blue-50 px-3 py-1.5 rounded-full"
                                >
                                    <PlusCircle size={14} /> Add Visa Record
                                </button>
                            </div>

                            {safeVisas.length === 0 ? (
                                <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                    <Globe size={40} className="text-gray-300 mx-auto mb-2" />
                                    <p className="text-gray-500 text-sm">No visa records added</p>
                                    <button
                                        type="button"
                                        onClick={addVisaEntry}
                                        className="mt-3 text-blue-600 text-sm font-medium"
                                    >
                                        + Add your first visa
                                    </button>
                                </div>
                            ) : (
                                safeVisas.map((v, i) => (
                                    <div key={i} className="group border border-gray-200 rounded-xl sm:rounded-2xl bg-white hover:border-blue-200 transition-all overflow-hidden">
                                        <div
                                            className="flex justify-between items-center p-4 sm:p-5 md:p-6 cursor-pointer md:cursor-default bg-gray-50/50"
                                            onClick={() => toggleVisaCard(i)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                                    {i + 1}
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                                        {v.country || "New Visa Application"}
                                                    </h4>
                                                    {v.visaType && (
                                                        <p className="text-[10px] sm:text-xs text-gray-500">{v.visaType}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    className="md:hidden text-gray-400"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleVisaCard(i);
                                                    }}
                                                >
                                                    <ChevronDown size={18} className={`transition-transform ${expandedCards[i] ? 'rotate-180' : ''}`} />
                                                </button>
                                                {safeVisas.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeVisaEntry(i);
                                                        }}
                                                        className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className={`
                                            transition-all duration-300 overflow-hidden
                                            ${expandedCards[i] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                                        `}>
                                            <div className="p-4 sm:p-5 md:p-6 pt-0 sm:pt-0 border-t border-gray-100">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                                    <div className="space-y-1.5">
                                                        <label className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wider ml-1">
                                                            Country *
                                                        </label>
                                                        <div className="relative">
                                                            <select
                                                                value={v.country}
                                                                onChange={(e) => handleVisaChange(i, "country", e.target.value)}
                                                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all appearance-none cursor-pointer text-sm"
                                                                required
                                                            >
                                                                <option value="">Select Country</option>
                                                                {countries.map(c => <option key={c} value={c}>{c}</option>)}
                                                            </select>
                                                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                        </div>
                                                    </div>

                                                    <InputField
                                                        label="Visa Type"
                                                        value={v.visaType}
                                                        onChange={(val) => handleVisaChange(i, "visaType", val)}
                                                        placeholder="e.g., Tourist, Work, Student"
                                                    />

                                                    <InputField
                                                        type="date"
                                                        label="Date of Issue"
                                                        value={v.issueDate}
                                                        max={new Date().toISOString().split('T')[0]}
                                                        onChange={(val) => handleVisaChange(i, "issueDate", val)}
                                                    />

                                                    <InputField
                                                        type="date"
                                                        label="Date of Expiry"
                                                        value={v.expiryDate}
                                                        min={v.issueDate}
                                                        onChange={(val) => handleVisaChange(i, "expiryDate", val)}
                                                    />

                                                    <div className="md:col-span-2 p-3 sm:p-4 bg-gray-50 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                                        <span className="text-xs sm:text-sm font-medium text-gray-700">
                                                            Have you ever been denied a visa for this country?
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleVisaChange(i, "denied", !v.denied)}
                                                            className={`w-12 h-6 sm:w-14 sm:h-7 rounded-full relative transition-all duration-300 flex-shrink-0 ${v.denied ? 'bg-rose-500' : 'bg-gray-300'}`}
                                                        >
                                                            <div className={`absolute top-1 bg-white w-4 h-4 sm:w-5 sm:h-5 rounded-full transition-all duration-300 ${v.denied ? 'left-6 sm:left-7' : 'left-1'}`} />
                                                        </button>
                                                    </div>

                                                    {v.denied && (
                                                        <div className="md:col-span-2 animate-in fade-in duration-200">
                                                            <label className="text-[10px] sm:text-[11px] font-semibold text-rose-600 uppercase tracking-wider ml-1">
                                                                Reason for Denial *
                                                            </label>
                                                            <textarea
                                                                value={v.denialReason}
                                                                onChange={(e) => handleVisaChange(i, "denialReason", e.target.value)}
                                                                className="w-full bg-white border border-rose-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-rose-500 focus:ring-1 focus:ring-rose-200 outline-none transition-all text-sm mt-1"
                                                                placeholder="Please explain why the visa was refused..."
                                                                rows={3}
                                                            />
                                                            {localErrors[`denialReason_${i}`] && (
                                                                <p className="text-rose-500 text-[10px] mt-1">{localErrors[`denialReason_${i}`]}</p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* OLD PASSPORT SECTION */}
                        <div className="space-y-5 sm:space-y-6 pt-4 border-t border-gray-100">
                            <h3 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                                <History size={16} className="text-blue-600" />
                                Previous Passports
                            </h3>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    onClick={() => setOldPassports({ ...oldPassports, hasOldPassport: true })}
                                    className={`flex-1 py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all ${oldPassports.hasOldPassport ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                >
                                    Yes, I have old passports
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setOldPassports({ ...oldPassports, hasOldPassport: false, passports: [] })}
                                    className={`flex-1 py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all ${!oldPassports.hasOldPassport ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                >
                                    No, I don't
                                </button>
                            </div>

                            {oldPassports.hasOldPassport && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 animate-in slide-in-from-top-4">
                                    {safeOldPassports.map((p, i) => (
                                        <div key={i} className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden">
                                            <div
                                                className="flex justify-between items-center p-4 cursor-pointer md:cursor-default"
                                                onClick={() => togglePassportCard(i)}
                                            >
                                                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                    Passport #{i + 1}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <button className="md:hidden text-gray-400">
                                                        <ChevronDown size={16} className={`transition-transform ${expandedPassports[i] ? 'rotate-180' : ''}`} />
                                                    </button>
                                                    {safeOldPassports.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removePassportEntry(i);
                                                            }}
                                                            className="text-rose-400 hover:text-rose-600 p-1"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={`
                                                transition-all duration-300 overflow-hidden
                                                ${expandedPassports[i] !== false ? 'max-h-[600px] opacity-100' : 'max-h-0 md:max-h-[600px] md:opacity-100'}
                                            `}>
                                                <div className="p-4 pt-0 space-y-3">
                                                    <InputField
                                                        label="Passport Number"
                                                        value={p.passportNumber}
                                                        onChange={(v) => handleOldPassportChange(i, "passportNumber", v)}
                                                        required
                                                    />
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        <InputField
                                                            type="date"
                                                            label="Issue Date"
                                                            value={p.issueDate}
                                                            max={new Date().toISOString().split('T')[0]}
                                                            onChange={(v) => handleOldPassportChange(i, "issueDate", v)}
                                                        />
                                                        <InputField
                                                            type="date"
                                                            label="Expiry Date"
                                                            value={p.expiryDate}
                                                            min={p.issueDate}
                                                            onChange={(v) => handleOldPassportChange(i, "expiryDate", v)}
                                                        />
                                                    </div>
                                                    <InputField
                                                        label="Remarks"
                                                        value={p.remarks}
                                                        onChange={(v) => handleOldPassportChange(i, "remarks", v)}
                                                        placeholder="Any additional notes"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addPassportEntry}
                                        className="border-2 border-dashed border-gray-300 rounded-xl sm:rounded-2xl p-6 text-gray-500 hover:text-blue-600 hover:border-blue-400 transition-all flex flex-col items-center gap-2 group"
                                    >
                                        <PlusCircle size={24} className="group-hover:scale-110 transition-transform" />
                                        <span className="text-[10px] sm:text-xs font-medium uppercase tracking-wider">Add Another Passport</span>
                                    </button>
                                </div>
                            )}
                        </div>

                    </form>
                )}
            </div>
        </div>
    );
};

/* REUSABLE COMPONENTS */
const InputField = ({ label, value, onChange, type = "text", placeholder, required, max, min }) => (
    <div className="space-y-1.5">
        <label className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wider ml-1">
            {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <input
            type={type}
            value={value || ""}
            max={max}
            min={min}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm placeholder:text-gray-400"
        />
    </div>
);

const PreviewItem = ({ label, value, light, isDate }) => (
    <div className="min-w-0">
        <p className={`text-[9px] sm:text-[10px] font-semibold uppercase mb-0.5 tracking-wider truncate ${light ? "text-white/50" : "text-gray-400"}`}>
            {label}
        </p>
        <p className={`text-xs sm:text-sm font-medium break-words ${light ? "text-white" : "text-gray-800"}`}>
            {isDate && value ? new Date(value).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : (value || "—")}
        </p>
    </div>
);

export default VISAForm;